import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import moment from 'moment-timezone';

import { createEntityAppointment, rescheduleAppointmentRecurrence } from 'api/Appointment';

import { formatDate } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Select from 'components/shared/Select/Select';

import { getHours } from '../../../../lib/getHours';

const Time = ({ currentPractice, className }) => {
  const {
    values,
    setFieldValue,
    handleSubmit,
    setAppointmentId,
    handleCloseEncounterModal,
    initialValues
  } = useFormikContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const currentTime = moment.tz(values.starts_at, currentPractice?.timezone).format('hh:mm A');
  const currentValue = { value: currentTime, label: currentTime };
  const hours = getHours(currentPractice);
  const options = hours.map((time) => ({ value: time, label: time }));
  const diffMin = moment(values?.ends_at).diff(moment(values?.starts_at), 'minutes');
  const isAptDisabled = values.appointment_disabled;

  const handleCreateEntity = async (params) => {
    const { code, updatedAppointment } = await createEntityAppointment(navigate, params);

    if (code === 0) {
      queryClient.invalidateQueries(['appointment-events']);
      showAlert({ color: 'success', message: 'Appointment updated successfully!' });
      setAppointmentId(updatedAppointment?.id);
    }

    if (code === 6) {
      showAlert({
        message: 'An appointment already exists at this time',
        color: 'warning',
        buttons: [
          {
            text: 'Reschedule anyway',
            onClick: () => handleCreateEntity({ ...params, isReschedule: false }),
            color: 'warning'
          }
        ]
      });
    }
  };

  const handleTimeChange = (selectedOption, type = null) => {
    const isRecurrence = !!values.isRecurrence;
    // Calculate the new date with the selected time
    const [timeString, period] = selectedOption.value.split(' ');
    const [hour, minute] = timeString.split(':');
    const newDate = moment
      .tz(values.starts_at, currentPractice?.timezone)
      .hour(period === 'PM' ? (parseInt(hour) % 12) + 12 : parseInt(hour) % 12)
      .minute(parseInt(minute))
      .second(0)
      .millisecond(0);

    if (isRecurrence && !type) {
      setIsModalOpen(true);
      setSelectedTime(newDate); // Store the entire date
      return;
    }

    const dateUTC = newDate.utc().format();

    if (type === 'only' && !values.parent_appointment_id) {
      setIsLoading(true);
      const params = {
        appointment_id: values.id,
        appointment_date: {
          starts_at: moment.tz(dateUTC, currentPractice?.timezone).format(),
          ends_at: moment.tz(dateUTC, currentPractice?.timezone).add(diffMin, 'm').format(),
          deleted_date: formatDate(initialValues.starts_at)
        },
        isReschedule: true
      };
      handleCreateEntity(params).then(() => {
        setTimeout(() => {
          queryClient.invalidateQueries(['appointment-events']);
        }, 1000);
        setIsLoading(false);
        setIsModalOpen(false);
        handleCloseEncounterModal();
      });
      return;
    }

    if (type === 'all') {
      setIsLoading(true);
      const params = {
        appointmentChanges: {
          id: values.id,
          starts_at: moment.tz(dateUTC, currentPractice?.timezone).format(),
          ends_at: moment.tz(dateUTC, currentPractice?.timezone).add(diffMin, 'm').format()
        },
        recurrence: {
          base_end_date: moment(values.starts_at).subtract(1, 'day').toDate(),
          base_appointment: values?.is_base_appointment
        }
      };
      rescheduleAppointmentRecurrence(navigate, params).then(({ code }) => {
        if (code === 0) {
          queryClient.invalidateQueries(['appointment-events']);
          setTimeout(() => {
            queryClient.invalidateQueries(['appointment-events']);
          }, 1000);
          handleCloseEncounterModal();
          showAlert({ color: 'success', message: 'Appointment updated successfully!' });
        }
        setIsLoading(false);
        setIsModalOpen(false);
      });

      return;
    }

    if (values.parent_appointment_id) {
      setFieldValue('starts_at', moment.tz(dateUTC, currentPractice?.timezone).format());
      setFieldValue(
        'ends_at',
        moment.tz(dateUTC, currentPractice?.timezone).add(diffMin, 'm').format()
      ).then(() => {
        handleSubmit();
        setIsLoading(false);
        setIsModalOpen(false);
        handleCloseEncounterModal();
      });

      return;
    }

    setFieldValue('starts_at', moment.tz(dateUTC, currentPractice?.timezone).format());
    setFieldValue(
      'ends_at',
      moment.tz(dateUTC, currentPractice?.timezone).add(diffMin, 'm').format()
    );
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Select
        parentClassName={className}
        label="Time"
        isClearable={false}
        rightIcon="clock-mono"
        options={options}
        value={currentValue}
        onChange={(val) => handleTimeChange(val)}
        disabled={isAptDisabled}
      />
      {isModalOpen && selectedTime && selectedTime.isAfter(moment()) ? (
        <Confirm
          title="You're changing a repeating appointment!"
          message={`You're changing the time of a repeating appointment. Do you want to move only this recurrence to ${selectedTime.format('hh:mm A')}, or change the time for this and all future appointments?`}
          handleOpen={isModalOpen}
          customClassName="w-[720px]"
          icon="calendar-dates"
          footer={
            <div className="flex items-center justify-between pt-4">
              <Button size="small" outlined text="Cancel" color="neutral" onClick={handleClose} />
              <div className="flex items-center gap-2">
                <Button
                  size="small"
                  text="Only this appointment"
                  color="primary"
                  onClick={() =>
                    handleTimeChange({ value: selectedTime.format('hh:mm A') }, 'only')
                  }
                  loading={isLoading}
                  disabled={isLoading}
                />
                <Button
                  size="small"
                  outlined
                  text="All future appointments"
                  color="primary"
                  onClick={() => handleTimeChange({ value: selectedTime.format('hh:mm A') }, 'all')}
                  loading={isLoading}
                  disabled={isLoading}
                />
              </div>
            </div>
          }
          handleClose={handleClose}
        />
      ) : (
        isModalOpen &&
        selectedTime && (
          <Confirm
            title="You're changing the time of a repeating appointment in the past!"
            message="You are changing the time of a repeating appointment to a time in the past. Do you want to move only this recurrence to the selected time?"
            handleOpen={isModalOpen}
            customClassName="w-[720px]"
            icon="calendar-dates"
            footer={
              <div className="flex items-center justify-between pt-4">
                <Button size="small" outlined text="Cancel" color="neutral" onClick={handleClose} />
                <Button
                  size="small"
                  text="Reschedule appointment"
                  color="primary"
                  onClick={() =>
                    handleTimeChange({ value: selectedTime.format('hh:mm A') }, 'only')
                  }
                  loading={isLoading}
                  disabled={isLoading}
                />
              </div>
            }
            handleClose={handleClose}
          />
        )
      )}
    </>
  );
};

export default Time;
